// The Book & pay form, kept across a reload or a return in the same browser.
//
// Robin, 9 Oct 2026: an Android visitor set car size and date at 06:08, came
// back at 07:51, pressed Pay and was refused for both again, while the
// address had been kept. A phone that drops a background tab reloads it, and
// a reload started the form from nothing. So every change on Book & pay is
// written down (store/booking-persistence.js) and read back here on the
// next load of /booking/book.
//
// Pure: what to keep and what is too old, pinned by booking-draft.test.mjs.
import { resolveBookingDate } from './date-input.js'

// A Book & pay draft is kept for a day; anything else (the old four-step
// screens' Stripe round trip) for two hours, as before.
export const BOOK_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000
export const OTHER_DRAFT_MAX_AGE_MS = 2 * 60 * 60 * 1000

// The form values a draft carries besides the store's booking state. The
// optional Registration box and the car it looked up (carLookup, see
// lib/vehicle.js) since 19 Oct 2026, so a return from Stripe keeps the plate.
// The Car location box, whether its address was picked and whether the
// customer chose the manual boxes since 19 Oct 2026 (lib/car-location.js), so
// a reload or a return from Stripe keeps the picked address.
// Since 25 Oct 2026 the Car location box is the only location box; postcode
// and address are still kept (derived from it, or a pick), and a draft from
// before with only those two comes back as one Car location
// (lib/car-location.js draftCarLocation).
// Where the car size came from (sizeSource, Form v3, 20 Oct 2026), so a
// size the plate set stays the plate's.
export const FORM_KEYS = ['carSize', 'congestionZone', 'mobile', 'notes', 'postcode', 'address', 'carLocation', 'addressPicked', 'locationManual', 'date', 'timeSlot', 'carRegistration', 'carLookup', 'sizeSource']

// The extra form values to save, from react-hook-form's values. Dates as
// ISO strings, nothing undefined.
export const formDraft = values => {
  const out = {}
  for (const key of FORM_KEYS) {
    const v = values?.[key]
    if (v === undefined) continue
    out[key] = v instanceof Date ? (isNaN(v) ? null : v.toISOString()) : v
  }
  return out
}

// A saved draft as it may be used at `now`, or null when there is none or it
// is too old. A day that can no longer be booked is dropped (the form then
// preselects tomorrow again), and the same-day slot goes with today.
export const usableDraft = (data, { now = new Date(), todayOpen = false, sameDaySlot = 'Anytime Today' } = {}) => {
  if (!data || typeof data !== 'object' || !data.savedAt) return null
  const maxAge = data.step === 'book' ? BOOK_DRAFT_MAX_AGE_MS : OTHER_DRAFT_MAX_AGE_MS
  if (now.getTime() - data.savedAt > maxAge) return null
  const out = { ...data }
  const raw = data.date ? new Date(data.date) : null
  if (!raw || isNaN(raw)) {
    out.date = null
    if (out.timeSlot === sameDaySlot) out.timeSlot = null
    return out
  }
  const { date, sameDay, corrected } = resolveBookingDate(raw, now, todayOpen)
  out.date = corrected ? null : date
  if (out.timeSlot === sameDaySlot && !sameDay) out.timeSlot = null
  return out
}
